import { supabase } from "../lib/supabase";
import {
	derived,
	writable,
	get,
	type Readable,
	type Writable
} from "svelte/store";
import type { Database } from "@typesAndInterfaces/database.types";
import { getUser } from "@stores/user";

// These two types are generated by the Supabase CLI
export type FavouritesInsert =
	Database["public"]["Tables"]["favourites"]["Insert"]; // This is what we can insert into the table
export const favourites: Writable<FavouritesInsert[]> = writable([]);
export const favouriteLoading: Writable<boolean> = writable(false);

export const listFavourites = async () => {
	favouriteLoading.set(true);
	const user = await getUser();

	if (!user) {
		favouriteLoading.set(false);

		return;
	}
	const { data, error } = await supabase
		.from("favourites")
		.select("job_uuid, user_id")
		.eq("user_id", user?.id);
	if (error) {
		favouriteLoading.set(false);
		console.error("Error listing favourites", error);
		return;
	}
	favouriteLoading.set(false);

	favourites.set(data);
	return data;
};

export const isFavourite = (job_uuid: string): Readable<boolean> => {
	return derived(favourites, ($favourites) => {
		return $favourites.some((favourite) => favourite.job_uuid === job_uuid);
	});
};

// This function toggles a job as a favourite. If it's already a favourite, it removes it. If it's not, it adds it.
// The store gets optimistically updated, so the UI updates immediately, and wasteful API calls are avoided.
export const toggleFavourite = async (job_uuid: string) => {
	const user = await getUser();

	if (!user) {
		return;
	}

	const isCurrentlyFavourite = get(isFavourite(job_uuid));

	if (isCurrentlyFavourite) {
		// First update the store to ensure UI consistency
		favourites.update((favourites) =>
			favourites.filter((favourite) => favourite.job_uuid !== job_uuid)
		);

		// Then make the API call
		const { error } = await supabase
			.from("favourites")
			.delete()
			.eq("job_uuid", job_uuid)
			.eq("user_id", user.id);

		if (error) {
			// If there's an error, revert the store update
			const favourite = { user_id: user.id, job_uuid: job_uuid };
			favourites.update((favs) => [...favs, favourite]);
			return;
		}
	} else {
		const favourite: FavouritesInsert = {
			user_id: user.id,
			job_uuid: job_uuid
		};

		// First update the store
		favourites.update((favourites) => [...favourites, favourite]);

		// Then make the API call
		const { error } = await supabase.from("favourites").insert([favourite]);
		if (error) {
			// If there's an error, revert the store update
			favourites.update((favs) => favs.filter((f) => f.job_uuid !== job_uuid));
			console.error("Error inserting favourite", error);
			return;
		}
	}
};
